import { useEffect, useState } from "react";
import { Bell, Check, Crown, Infinity as InfinityIcon, LineChart, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, isUpgradeError, type BillingInterval } from "@/lib/api";
import { PRICING } from "@/hooks/usePlan";
import { Button } from "@/components/ui/button";
import { pushEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

// ───────────────────────── imperative open() ─────────────────────────
// A tiny module-level event bus so any call site (a 402 catch, a "Passer Pro"
// button) can pop the dialog without prop-drilling. Mount <UpgradeDialog />
// once near the app root.

type OpenOptions = { reason?: string };
type Listener = (opts: OpenOptions) => void;
const listeners = new Set<Listener>();

export function openUpgrade(opts: OpenOptions = {}) {
  listeners.forEach((l) => l(opts));
}

/**
 * Convenience: in a mutation onError, call this. If the error is a 402
 * upgrade_required it opens the dialog (and returns true) ; otherwise it does
 * nothing and returns false so the caller can show its normal error toast.
 */
export function handleUpgradeError(e: unknown): boolean {
  if (isUpgradeError(e)) {
    const msg = (e.body as any)?.message as string | undefined;
    openUpgrade({ reason: msg });
    return true;
  }
  return false;
}

// ───────────────────────── component ─────────────────────────

const FEATURES = [
  { icon: InfinityIcon, text: "Suivis illimités (forums, fils, utilisateurs)" },
  { icon: Bell, text: "Alertes en temps réel — soyez prévenu à la seconde" },
  { icon: LineChart, text: "Historique complet + graphiques sans limite" },
  { icon: Check, text: "Seuils d'alerte personnalisés & export CSV" },
];

export function UpgradeDialog() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState<BillingInterval | null>(null);

  useEffect(() => {
    const listener: Listener = (opts) => {
      setReason(opts.reason ?? null);
      setOpen(true);
      pushEvent("upgrade_open", { source: opts.reason ?? "manual" });
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  async function checkout(interval: BillingInterval) {
    setBusy(interval);
    pushEvent("begin_checkout", { billing_interval: interval });
    try {
      const { url } = await api.billingCheckout(interval);
      window.location.assign(url);
    } catch (e: any) {
      const code = e?.body?.error;
      if (code === "billing_unavailable" || code === "price_not_configured") {
        toast.error("Le paiement n'est pas encore activé. Réessayez bientôt.");
      } else {
        toast.error("Impossible d'ouvrir le paiement. Réessayez.");
      }
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-md rounded-lg border border-border-strong bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-4 top-4 text-foreground-muted hover:text-foreground"
          onClick={() => setOpen(false)}
          aria-label="Fermer"
        >
          <X className="size-5" />
        </button>

        <div className="flex items-center gap-2 text-primary">
          <Crown className="size-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">Boursicote Pro</span>
        </div>

        <h2 className="mt-2 text-xl font-semibold text-foreground">
          Passez en illimité
        </h2>
        <p className="mt-1 text-sm text-foreground-muted">
          {reason ??
            "Débloquez le suivi illimité et les alertes en temps réel pour ne plus rien rater de vos forums."}
        </p>

        <ul className="mt-5 flex flex-col gap-3">
          {FEATURES.map((f) => (
            <li key={f.text} className="flex items-start gap-3">
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <f.icon className="size-3.5" />
              </span>
              <span className="text-sm text-foreground">{f.text}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            className="h-11 w-full justify-between"
            disabled={busy !== null}
            onClick={() => checkout("monthly")}
          >
            <span className="flex items-center gap-2">
              {busy === "monthly" && <Loader2 className="size-4 animate-spin" />}
              Mensuel
            </span>
            <span className="font-semibold">
              {PRICING.monthly.amount}/{PRICING.monthly.period}
            </span>
          </Button>
          <Button
            variant="outline"
            className="h-11 w-full justify-between"
            disabled={busy !== null}
            onClick={() => checkout("yearly")}
          >
            <span className="flex items-center gap-2">
              {busy === "yearly" && <Loader2 className="size-4 animate-spin" />}
              Annuel
            </span>
            <span className={cn("font-semibold")}>
              {PRICING.yearly.amount}/{PRICING.yearly.period}
            </span>
          </Button>
        </div>

        <p className="mt-3 text-center text-xs text-foreground-muted">
          {PRICING.monthly.note} · Paiement sécurisé via Stripe · Données hébergées en France
        </p>
      </div>
    </div>
  );
}
